import * as Sentry from '@sentry/react';
import { logger } from 'utils/logger';
import { JanusJS } from 'lib/janus';
import { IceDeathState, IceRecoverySnapshot } from 'utils/webrtc/IceRecoveryLadder';
import { emitTurnUnauthorized, IceFeedType } from 'utils/webrtc/iceTelemetry';
import { selectedCandidatePair } from 'utils/webrtc/stats/selectedCandidatePair';
import { turnCredentialRemainingSec, turnUnauthorizedErrors } from 'utils/webrtc/turnCredentials';

export interface CandidateInfo {
  type: string;
  protocol: string;
  address: string;
  port?: number;
  relayProtocol?: string;
  url?: string;
}

export interface CandidatePair {
  local: CandidateInfo;
  remote: CandidateInfo;
}

export interface IceCompletionResult {
  isCompleted: boolean;
  selectedCandidate?: CandidatePair;
  nominatedPairs: number;
  successfulPairs: number;
  inProgressPairs: number;
  completionConfidence: 'high' | 'medium' | 'low';
  browserSupport: {
    hasNominationSupport: boolean;
    hasSelectedCandidatePairId: boolean;
    hasFullStatsSupport: boolean;
  };
}

export interface RelayFallbackTelemetry extends IceRecoverySnapshot {
  reason: IceDeathState;
  connectedLifetimeMs: number | null;
  stableReset: boolean;
}

// Helper function to create candidate info with privacy considerations
function createCandidateInfo(candidate: any): CandidateInfo {
  return {
    type: candidate.candidateType || 'unknown',
    protocol: candidate.protocol || 'unknown',
    port: candidate.port,
    address: candidate.address,
    relayProtocol: candidate.relayProtocol,
    url: candidate.url,
  };
}

// Helper function to create candidate pair from IDs
export function createCandidatePair(
  localId: string,
  remoteId: string,
  candidates: Map<string, any>
): CandidatePair | null {
  const localCandidate = candidates.get(localId);
  const remoteCandidate = candidates.get(remoteId);

  if (localCandidate && remoteCandidate) {
    return {
      local: createCandidateInfo(localCandidate),
      remote: createCandidateInfo(remoteCandidate),
    };
  }
  return null;
}

// Function to build gathered candidates array (all candidate pairs)
function buildGatheredCandidates(
  candidatePairs: any[],
  candidates: Map<string, any>
): CandidatePair[] {
  const gatheredCandidates: CandidatePair[] = [];

  candidatePairs.forEach((pair) => {
    if (pair.localCandidateId && pair.remoteCandidateId) {
      const candidatePair = createCandidatePair(
        pair.localCandidateId,
        pair.remoteCandidateId,
        candidates
      );
      if (candidatePair) {
        gatheredCandidates.push(candidatePair);
      }
    }
  });

  return gatheredCandidates;
}

// Function to build successful candidates array (only successful pairs)
function buildSuccessfulCandidates(
  candidatePairs: any[],
  candidates: Map<string, any>
): CandidatePair[] {
  const successfulCandidates: CandidatePair[] = [];

  candidatePairs.forEach((pair) => {
    if (pair.state === 'succeeded' && pair.localCandidateId && pair.remoteCandidateId) {
      const candidatePair = createCandidatePair(
        pair.localCandidateId,
        pair.remoteCandidateId,
        candidates
      );
      if (candidatePair) {
        successfulCandidates.push(candidatePair);
      }
    }
  });

  return successfulCandidates;
}

export class IceLogger {
  /**
   * Detects when ICE negotiation is truly completed with a stable, nominated candidate pair.
   * Uses default configuration optimized for cross-browser compatibility.
   */
  static async detectIceCompletion(
    peerConnection: RTCPeerConnection
  ): Promise<IceCompletionResult> {
    if (!peerConnection.getStats) {
      return {
        isCompleted: false,
        nominatedPairs: 0,
        successfulPairs: 0,
        inProgressPairs: 0,
        completionConfidence: 'low',
        browserSupport: {
          hasNominationSupport: false,
          hasSelectedCandidatePairId: false,
          hasFullStatsSupport: false,
        },
      };
    }

    const maxAttempts = 20;
    const intervalMs = 250;
    const stabilityWindowMs = 750;
    const initialDelayMs = 500;

    return new Promise((resolve) => {
      let attempt = 0;
      let lastStableResult: IceCompletionResult | null = null;
      let stabilityStartTime: number | null = null;
      let hasFoundSuccessfulConnection = false;

      const pollForCompletion = async () => {
        attempt += 1;

        try {
          const result = await this.analyzeIceStats(peerConnection);

          // Track if we've ever had successful pairs
          if (result.successfulPairs > 0) {
            hasFoundSuccessfulConnection = true;
          }

          // Accept first successful result as stable
          const isStable = this.isResultStable(
            result,
            lastStableResult,
            hasFoundSuccessfulConnection
          );

          if (
            isStable &&
            (result.isCompleted || this.shouldAcceptAsCompleted(result, attempt, maxAttempts))
          ) {
            if (!stabilityStartTime) {
              stabilityStartTime = Date.now();
            }

            // Check if we've maintained stability for the required window
            const stabilityDuration = Date.now() - stabilityStartTime;
            if (stabilityDuration >= stabilityWindowMs) {
              // If not officially completed but we have good conditions, upgrade the result
              const finalResult: IceCompletionResult = result.isCompleted
                ? result
                : {
                    isCompleted: true,
                    selectedCandidate: result.selectedCandidate,
                    nominatedPairs: result.nominatedPairs,
                    successfulPairs: result.successfulPairs,
                    inProgressPairs: result.inProgressPairs,
                    completionConfidence:
                      result.completionConfidence === 'high' ? 'high' : 'medium',
                    browserSupport: result.browserSupport,
                  };
              resolve(finalResult);
              return;
            }
          } else {
            // Reset stability timer if result changed
            stabilityStartTime = null;
          }

          lastStableResult = result;

          // Continue polling if we haven't reached max attempts
          if (attempt < maxAttempts) {
            setTimeout(pollForCompletion, intervalMs);
          } else {
            // Return the best result we have, even if not fully stable
            const finalResult = lastStableResult || result;
            // If we have successful pairs and reasonable conditions, mark as completed
            if (finalResult.successfulPairs > 0 && finalResult.inProgressPairs === 0) {
              const completedResult: IceCompletionResult = {
                isCompleted: true,
                selectedCandidate: finalResult.selectedCandidate,
                nominatedPairs: finalResult.nominatedPairs,
                successfulPairs: finalResult.successfulPairs,
                inProgressPairs: finalResult.inProgressPairs,
                completionConfidence: 'medium',
                browserSupport: finalResult.browserSupport,
              };
              resolve(completedResult);
            } else {
              resolve(finalResult);
            }
          }
        } catch (error) {
          logger.warn('Error during ICE completion detection:', error);
          resolve({
            isCompleted: false,
            nominatedPairs: 0,
            successfulPairs: 0,
            inProgressPairs: 0,
            completionConfidence: 'low',
            browserSupport: {
              hasNominationSupport: false,
              hasSelectedCandidatePairId: false,
              hasFullStatsSupport: false,
            },
          });
        }
      };

      // Add initial delay to let WebRTC stats populate properly
      setTimeout(pollForCompletion, initialDelayMs);
    });
  }

  /**
   * Determines if we should accept the current result as completed even if not officially detected.
   * This provides more lenient completion criteria for edge cases.
   */
  private static shouldAcceptAsCompleted(
    result: IceCompletionResult,
    currentAttempt: number,
    maxAttempts: number
  ): boolean {
    // If we're past halfway through polling and have good conditions
    if (currentAttempt > maxAttempts / 2) {
      // Accept if we have successful pairs and no in-progress pairs
      if (result.successfulPairs > 0 && result.inProgressPairs === 0) {
        return true;
      }

      // Accept if we have strong browser support and successful pairs
      if (
        result.successfulPairs > 0 &&
        (result.browserSupport.hasSelectedCandidatePairId ||
          result.browserSupport.hasNominationSupport)
      ) {
        return true;
      }
    }

    // Near the end of polling, accept any successful connection
    return currentAttempt > maxAttempts * 0.8 && result.successfulPairs > 0;
  }

  /**
   * Unified method to analyze ICE stats for both completion detection and connection success logging.
   * Handles cross-browser differences in stats reporting.
   */
  private static async analyzeIceStats(
    peerConnection: RTCPeerConnection,
    includeGatheredCandidates = false
  ): Promise<
    IceCompletionResult & {
      gatheredCandidates?: CandidatePair[];
      successfulCandidates?: CandidatePair[];
    }
  > {
    const stats = await peerConnection.getStats();

    let nominatedPairs = 0;
    let successfulPairs = 0;
    let inProgressPairs = 0;

    const candidates = new Map<string, any>();
    const candidatePairs: any[] = [];

    // Browser capability detection
    let hasNominationSupport = false;
    let hasSelectedCandidatePairId = false;
    const hasFullStatsSupport = true;

    // First pass: collect all data and detect browser capabilities
    stats.forEach((report: any) => {
      if (report.type === 'local-candidate' || report.type === 'remote-candidate') {
        candidates.set(report.id, report);
      } else if (report.type === 'candidate-pair') {
        candidatePairs.push(report);

        // Detect browser capabilities
        if (report.nominated !== undefined) {
          hasNominationSupport = true;
        }

        // Count pairs by state
        if (report.state === 'succeeded') {
          successfulPairs += 1;
          if (report.nominated) {
            nominatedPairs += 1;
          }
        } else if (report.state === 'in-progress' || report.state === 'waiting') {
          inProgressPairs += 1;
        }
      } else if (report.type === 'transport') {
        // Check for selectedCandidatePairId (Chrome/Edge)
        if (report.selectedCandidatePairId) {
          hasSelectedCandidatePairId = true;
        }
      }
    });

    const selectedPair = selectedCandidatePair(stats);

    // Determine completion status based on browser capabilities
    const isCompleted = this.determineCompletionStatus(
      nominatedPairs,
      successfulPairs,
      inProgressPairs,
      selectedPair,
      hasNominationSupport,
      hasSelectedCandidatePairId
    );

    // Calculate confidence level
    const completionConfidence = this.calculateCompletionConfidence(
      isCompleted,
      nominatedPairs,
      successfulPairs,
      inProgressPairs,
      hasNominationSupport,
      hasSelectedCandidatePairId
    );

    // Extract selected candidate details
    let selectedCandidate: CandidatePair | undefined;
    if (selectedPair && selectedPair.localCandidateId && selectedPair.remoteCandidateId) {
      selectedCandidate =
        createCandidatePair(
          selectedPair.localCandidateId,
          selectedPair.remoteCandidateId,
          candidates
        ) || undefined;
    }

    const result: any = {
      isCompleted,
      selectedCandidate,
      nominatedPairs,
      successfulPairs,
      inProgressPairs,
      completionConfidence,
      browserSupport: {
        hasNominationSupport,
        hasSelectedCandidatePairId,
        hasFullStatsSupport,
      },
    };

    // Include candidate arrays only when requested (for connection success logging)
    if (includeGatheredCandidates) {
      result.gatheredCandidates = buildGatheredCandidates(candidatePairs, candidates);
      result.successfulCandidates = buildSuccessfulCandidates(candidatePairs, candidates);
    }

    return result;
  }

  /**
   * Determines if ICE negotiation is completed based on available data and browser capabilities.
   */
  private static determineCompletionStatus(
    nominatedPairs: number,
    successfulPairs: number,
    inProgressPairs: number,
    selectedPair: any,
    hasNominationSupport: boolean,
    hasSelectedCandidatePairId: boolean
  ): boolean {
    // No successful pairs means definitely not completed
    if (successfulPairs === 0) {
      return false;
    }

    // If we have many in-progress pairs relative to successful ones, negotiation may still be ongoing
    // But don't reject if we have a clear successful connection
    if (inProgressPairs > 0 && successfulPairs === 0) {
      return false;
    }

    // If we have more in-progress than successful and no strong browser support, be cautious
    if (inProgressPairs > successfulPairs && !hasSelectedCandidatePairId && !hasNominationSupport) {
      return false;
    }

    // High confidence completion criteria
    if (hasSelectedCandidatePairId && selectedPair) {
      return true;
    }

    if (hasNominationSupport && nominatedPairs > 0) {
      return true;
    }

    // Firefox's report.selected indicates completion
    if (selectedPair && selectedPair.selected) {
      return true;
    }

    // Medium confidence: we have successful pairs and no in-progress pairs
    // This is the best we can do in Safari and older browsers
    if (successfulPairs > 0 && inProgressPairs === 0) {
      return true;
    }

    return false;
  }

  /**
   * Calculates the confidence level of the completion detection.
   */
  private static calculateCompletionConfidence(
    isCompleted: boolean,
    nominatedPairs: number,
    successfulPairs: number,
    inProgressPairs: number,
    hasNominationSupport: boolean,
    hasSelectedCandidatePairId: boolean
  ): 'high' | 'medium' | 'low' {
    if (!isCompleted) {
      return 'low';
    }

    // High confidence: modern browser with full stats support
    if (hasSelectedCandidatePairId || (hasNominationSupport && nominatedPairs > 0)) {
      return 'high';
    }

    // Medium confidence: successful pairs with no in-progress pairs
    if (successfulPairs > 0 && inProgressPairs === 0) {
      return 'medium';
    }

    return 'low';
  }

  /**
   * Checks if the current result is stable compared to the previous result.
   */
  /**
   * Determines if the current ICE detection result is stable compared to the previous result.
   * A result is considered stable if the key metrics haven't changed between polls.
   */
  private static isResultStable(
    current: IceCompletionResult,
    previous: IceCompletionResult | null,
    hasFoundSuccessfulConnection: boolean
  ): boolean {
    // First result: stable if we have successful connections
    if (!previous) {
      return hasFoundSuccessfulConnection && current.successfulPairs > 0;
    }

    // Key metrics that indicate stability
    const keyMetricsUnchanged =
      current.isCompleted === previous.isCompleted &&
      current.successfulPairs === previous.successfulPairs &&
      current.inProgressPairs === previous.inProgressPairs &&
      current.nominatedPairs === previous.nominatedPairs;

    // Confidence and browser support should also be stable
    const metadataUnchanged =
      current.completionConfidence === previous.completionConfidence &&
      current.browserSupport.hasSelectedCandidatePairId ===
        previous.browserSupport.hasSelectedCandidatePairId &&
      current.browserSupport.hasNominationSupport === previous.browserSupport.hasNominationSupport;

    // Must have some successful connection to be considered stable
    const hasValidConnection = current.successfulPairs > 0;

    return keyMetricsUnchanged && metadataUnchanged && hasValidConnection;
  }

  static logGatheringComplete(
    handleId: string,
    feedType: IceFeedType,
    candidateStats: any,
    iceServers?: RTCIceServer[],
    peerConnection?: RTCPeerConnection
  ) {
    if (!candidateStats) {
      return;
    }

    const { gathered } = candidateStats;
    const hasGatheredCandidates = gathered > 0;
    const credentialRemainingSec = turnCredentialRemainingSec(iceServers);
    const unauthorized = turnUnauthorizedErrors(candidateStats.candidateErrors);

    // Candidate errors are kept per gathering, so this fires at most once per gathering.
    if (unauthorized.length > 0) {
      emitTurnUnauthorized({
        feedType,
        errorCount: unauthorized.length,
        url: unauthorized[0].url ?? null,
        credentialRemainingSec,
        relayOnly: peerConnection?.getConfiguration().iceTransportPolicy === 'relay',
      });
    }

    const turnServers = iceServers
      ? iceServers
          .filter((server) => {
            if (!server.urls) {
              return false;
            }
            const urls = Array.isArray(server.urls) ? server.urls : [server.urls];
            return urls.some((url: string) => url.includes('turn'));
          })
          .map((server) => (Array.isArray(server.urls) ? server.urls[0] : server.urls))
      : [];

    const logData: any = {
      handleId,
      feedType,
      candidatesGathered: gathered,
      turnServers,
      turnCredentialRemainingSec: credentialRemainingSec,
    };

    logData.candidateErrors = candidateStats.candidateErrors ?? [];

    if (!hasGatheredCandidates) {
      logger
        .remote({
          system: true,
          capture: 'ice-diagnostics',
        })
        .error('ICE gathering completed without any candidates', {
          ...logData,
          message:
            'No ICE candidates gathered. Check network connectivity and STUN/TURN server configuration.',
        });
      return;
    }

    // Add gathered candidates information if peer connection is available
    if (peerConnection) {
      this.analyzeIceStats(peerConnection, true)
        .then((result) => {
          if (result.gatheredCandidates && result.gatheredCandidates.length > 0) {
            logData.gatheredCandidates = result.gatheredCandidates;
          }

          logger
            .remote({
              system: true,
              capture: 'ice-diagnostics',
            })
            .debug('ICE gathering completed', logData);
        })
        .catch(() => {
          // Fallback to basic logging if stats analysis fails
          logger
            .remote({
              system: true,
              capture: 'ice-diagnostics',
            })
            .debug('ICE gathering completed', logData);
        });
    } else {
      logger
        .remote({
          system: true,
          capture: 'ice-diagnostics',
        })
        .debug('ICE gathering completed', logData);
    }
  }

  static async logConnectionSuccess(
    handleId: string,
    feedType: string,
    stats: JanusJS.IceConnectionStats,
    peerConnection?: RTCPeerConnection
  ) {
    const { duration, gathered } = stats;

    // Start ICE completion detection if peer connection is provided
    if (peerConnection) {
      const startTime = Date.now();

      try {
        const completionResult = await this.detectIceCompletion(peerConnection);
        const detectionDuration = Date.now() - startTime;

        // Log the completion detection results
        this.logIceCompletion(handleId, feedType, completionResult, detectionDuration);

        // Get detailed candidate information (successful candidates and selected candidate)
        const detailedResult = await this.analyzeIceStats(peerConnection, true);

        const logData: any = {
          handleId,
          feedType,
          candidatesGathered: gathered,
          candidatesSuccessful: detailedResult.successfulPairs,
          message: `ICE connection established in ${duration}ms`,
        };

        // Include detailed candidate information (gatheredCandidates are already logged in logGatheringComplete)
        if (detailedResult.successfulCandidates && detailedResult.successfulCandidates.length > 0) {
          logData.successfulCandidates = detailedResult.successfulCandidates;
        }

        if (detailedResult.selectedCandidate) {
          logData.selectedCandidate = detailedResult.selectedCandidate;
        }

        logger
          .remote({
            system: true,
            capture: 'ice-diagnostics',
            tier: 1,
          })
          .info('ICE connection established successfully', logData);
      } catch (error) {
        logger.warn('Failed to analyze ICE completion:', error);

        // Fallback to basic logging
        logger
          .remote({
            system: true,
            capture: 'ice-diagnostics',
            tier: 1,
          })
          .info('ICE connection established successfully', {
            handleId,
            feedType,
            candidatesGathered: gathered,
            message: `ICE connection established in ${duration}ms`,
          });
      }
    } else {
      // Basic logging without peer connection analysis
      logger
        .remote({
          system: true,
          capture: 'ice-diagnostics',
          tier: 1,
        })
        .info('ICE connection established successfully', {
          handleId,
          feedType,
          candidatesGathered: gathered,
          message: `ICE connection established in ${duration}ms`,
        });
    }
  }

  /**
   * Logs ICE completion detection results with detailed analysis.
   */
  static logIceCompletion(
    handleId: string,
    feedType: string,
    result: IceCompletionResult,
    detectionDurationMs: number
  ) {
    const logData: any = {
      handleId,
      feedType,
      isCompleted: result.isCompleted,
      nominatedPairs: result.nominatedPairs,
      successfulPairs: result.successfulPairs,
      inProgressPairs: result.inProgressPairs,
      completionConfidence: result.completionConfidence,
      detectionDurationMs,
      browserSupport: result.browserSupport,
    };

    if (result.selectedCandidate) {
      logData.selectedCandidate = result.selectedCandidate;
    }

    const logLevel = result.isCompleted ? 'info' : 'warn';
    const message = result.isCompleted
      ? `ICE negotiation completed with ${result.completionConfidence} confidence`
      : 'ICE negotiation completion could not be reliably detected';

    logger
      .remote({
        system: true,
        capture: 'ice-diagnostics',
        tier: 1,
      })
      [logLevel](message, logData);

    if (
      result.browserSupport.hasNominationSupport &&
      result.successfulPairs > 0 &&
      result.nominatedPairs === 0
    ) {
      const errorMessage = '[NO-NOMINATION] ICE connected without a nominated candidate pair';

      logger.remote({ system: true, capture: 'ice-diagnostics' }).warn(errorMessage, logData);

      Sentry.captureMessage(errorMessage, {
        level: 'warning',
        contexts: { iceNomination: logData },
      });
    }
  }

  static logConnectionFailure(
    handleId: string,
    feedType: string,
    state: RTCIceConnectionState,
    stats?: JanusJS.IceConnectionStats
  ) {
    const getMessage = (connectionState: RTCIceConnectionState) => {
      switch (connectionState) {
        case 'failed':
          return 'ICE connection failed - TURN servers may be unreachable or network connectivity issues';
        case 'disconnected':
          return 'ICE connection lost - network issues detected, connection may recover';
        default:
          return `ICE connection issue: ${connectionState}`;
      }
    };

    logger
      .remote({
        system: true,
        capture: 'ice-diagnostics',
      })
      .error('ICE connection failed', {
        handleId,
        feedType,
        state,
        candidatesGathered: stats?.gathered ?? 0,
        nonHostCandidatesGathered: stats?.nonHostGathered ?? 0,
        previouslyConnected: stats?.previouslyConnected ?? false,
        iceCompleted: stats?.iceCompleted ?? false,
        durationMs: stats?.duration,
        candidateErrors: stats?.candidateErrors ?? [],
        message: getMessage(state),
      });
  }

  static logRelayFallbackTriggered(
    handleId: string,
    feedType: string,
    data: RelayFallbackTelemetry
  ) {
    const logData = { handleId, feedType, ...data };

    logger
      .remote({ system: true, capture: 'streaming' })
      .warn('ICE relay fallback triggered', logData);

    Sentry.addBreadcrumb({
      category: 'ice-fallback',
      message: 'fallback-triggered',
      level: 'warning',
      data: logData,
    });
  }

  // Janus' hangup `reason` ("ICE failed", "Close PC", "DTLS alert", ...) tells
  // whether the server gave up on the PC itself or tore it down for another cause.
  static logPeerConnectionDown(
    handleId: string | undefined,
    feedType: string,
    data: { reason?: string; iceRestartAttempt: boolean; mediaFailureReported: boolean }
  ) {
    logger
      .remote({ system: true, capture: 'streaming' })
      .warn('Janus hung up the peer connection', { handleId, feedType, ...data });
  }

  static async logRelayConnected(
    handleId: string,
    feedType: string,
    peerConnection?: RTCPeerConnection
  ) {
    try {
      // Polls until the pair settles: a bare analyzeIceStats at the first
      // 'connected' still reports the pre-restart pair, so a relay-only PC would
      // log a host/srflx candidate it cannot possibly have gathered.
      const completion = peerConnection
        ? await this.detectIceCompletion(peerConnection).catch(() => undefined)
        : undefined;

      const logData = { handleId, feedType, selectedCandidate: completion?.selectedCandidate };

      logger
        .remote({ system: true, capture: 'streaming' })
        .info('ICE relay fallback connected', logData);

      Sentry.addBreadcrumb({
        category: 'ice-fallback',
        message: 'relay-connected',
        level: 'info',
        data: logData,
      });
    } catch {
      // Called bare from an ICE state handler: a throw here would surface as an
      // unhandled rejection.
    }
  }

  static logConnectionState(handleId: string, feedType: string, state: RTCIceConnectionState) {
    // Log all state changes for debugging, but with different severity levels
    const getLogLevel = (connectionState: RTCIceConnectionState) => {
      switch (connectionState) {
        case 'connected':
          return 'info';
        case 'failed':
        case 'disconnected':
          return 'warn';
        default:
          return 'debug';
      }
    };

    const getMessage = (connectionState: RTCIceConnectionState) => {
      switch (connectionState) {
        case 'connected':
          return 'ICE connection established successfully';
        case 'failed':
          return 'ICE connection failed - TURN servers may be unreachable';
        case 'disconnected':
          return 'ICE connection lost - network issues detected';
        case 'checking':
          return 'ICE connectivity checks in progress';
        default:
          return `ICE connection state: ${connectionState}`;
      }
    };

    const logLevel = getLogLevel(state);
    const message = getMessage(state);

    logger
      .remote({
        system: true,
        capture: 'ice-diagnostics',
      })
      [logLevel](`${feedType} feed: ice state: ${state}`, {
        handleId,
        feedType,
        state,
        message,
      });
  }
}
